/**
 * Règle de visibilité « confidentiel vétérinaire » des notes — côté web.
 *
 * Une note de santé rédigée par un vétérinaire peut être marquée « confidentielle »
 * (affichée uniquement avec le droit vétérinaire) via le champ `restrictedToVet`.
 *
 * Décision produit du 04/10/2026 : « tout ce qui est vétérinaire relève du droit
 * vétérinaire ». Ces notes ne sont plus affichées aux responsables (`admin`) sans
 * le droit vétérinaire ; un responsable qui en a besoin s'attribue ce droit depuis
 * sa fiche. C'est une restriction d'AFFICHAGE : les règles Firestore ne filtrent
 * pas les lectures de notes par requête, et les Cloud Functions sont inchangées.
 *
 * Ce champ est DISTINCT du type ZIMS `isMedical` : une note peut être ZIMS-medical
 * et néanmoins visible des soigneurs, ou confidentielle sans ZIMS. À la création,
 * cocher « ZIMS medical » pré-coche la confidentialité, mais le véto peut la lever.
 *
 * Rétro-compatibilité : les anciennes notes ne portent pas `restrictedToVet`. Pour
 * elles, on retombe sur le legacy `isMedical === true` (notes ZIMS médicales), qui
 * étaient déjà masquées aux soigneurs → aucun changement de visibilité, pas de migration.
 *
 * ⚠️ POINT DE VÉRITÉ UNIQUE : toute lecture de notes DOIT décider de la visibilité via
 * ce helper. Ne jamais réintroduire de check `isMedical` inline, sous peine de laisser
 * fuiter une note confidentielle (ou d'en masquer une à tort).
 */

/**
 * La note est-elle réservée au droit vétérinaire ?
 * - `restrictedToVet === true`  → restreinte (nouveau format, fait foi)
 * - `restrictedToVet === false` → publique (override explicite, ex. note ZIMS-medical partagée)
 * - `restrictedToVet` absent     → legacy : on retombe sur `isMedical === true`
 */
export function isNoteRestrictedToVet(note) {
  if (!note) return false;
  if (note.restrictedToVet === true) return true;
  if (note.restrictedToVet === false) return false;
  return note.isMedical === true; // legacy ZIMS-medical
}

/** Rôles autorisés à accéder au module Vétérinaire sans le droit vet (accès technique). */
const VET_MODULE_BYPASS_ROLES = ["superAdmin"];

/**
 * L'utilisateur peut-il accéder au module Vétérinaire (bloc latéral + VeterinaryDashboardView) ?
 * → droit vétérinaire (rights.vet), superAdmin, ou staff AKONGO (qui voit tout —
 * décision produit du 04/10/2026). Même périmètre que `canSeeRestrictedNotes` :
 * un responsable SANS droit vet n'a ni le module ni les notes confidentielles.
 *
 * @param {{ userRoleValue?: string, userRights?: { vet?: boolean }, akongoStaff?: boolean }} viewer
 */
export function canAccessVetModule(viewer = {}) {
  const { userRoleValue, userRights } = viewer;
  if (viewer.akongoStaff === true) return true;
  if (VET_MODULE_BYPASS_ROLES.includes(userRoleValue)) return true;
  return userRights?.vet === true;
}

/**
 * L'utilisateur courant peut-il voir les notes « Confidentiel vétérinaire » ?
 * → droit vétérinaire, superAdmin ou staff AKONGO (décision produit du
 * 04/10/2026) : le rôle `admin` (« Responsable ») ne suffit plus. Le viewer doit
 * porter `userRights` et `akongoStaff` (cf. `viewerFromUserData`).
 *
 * @param {{ userRoleValue?: string, userRights?: { vet?: boolean }, akongoStaff?: boolean }} viewer
 */
export function canSeeRestrictedNotes(viewer = {}) {
  return canAccessVetModule(viewer || {});
}

/**
 * Faut-il masquer cette note à cet utilisateur ? (true = ne pas afficher)
 *
 * @param {object} note
 * @param {{ userRoleValue?: string, userRights?: { vet?: boolean }, akongoStaff?: boolean }} viewer
 */
export function isNoteHiddenFromUser(note, viewer) {
  return isNoteRestrictedToVet(note) && !canSeeRestrictedNotes(viewer);
}

/**
 * Retire d'une liste de journaux (`zoo/{zooId}/logs`) les notes que cet utilisateur
 * ne doit pas voir. Les logs de note portent `restrictedToVet` / `isMedical`
 * (propagés par `logManagement`) : les galeries par journaux (enclos, secteur,
 * groupe, globale) doivent appliquer la même règle que les onglets Notes.
 *
 * @param {object[]} logs
 * @param {{ userRoleValue?: string, userRights?: { vet?: boolean } }} viewer
 */
export function filterLogsVisibleToUser(logs, viewer) {
  return (logs || []).filter((log) => !isNoteHiddenFromUser(log, viewer));
}

/**
 * Normalise un objet `userData` (formats variés rencontrés dans l'app) vers la
 * forme attendue par `canSeeRestrictedNotes` / `isNoteHiddenFromUser`.
 */
export function viewerFromUserData(userData = {}) {
  return {
    userRoleValue: userData?.userRight?.value || userData?.userRight || userData?.role,
    userRights: userData?.rights || userData?.userRights,
    akongoStaff: userData?.akongoStaff === true,
  };
}

/**
 * Viewer de la session courante (copie `userData` du localStorage : parc actif,
 * rôle et droits de ce parc). Pour les écrans qui n'ont pas le profil sous la
 * main (compteurs). Profil illisible → viewer vide : on masque (échec fermé).
 */
export function viewerFromSession() {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem("userData") : null;
    return viewerFromUserData(raw ? JSON.parse(raw) || {} : {});
  } catch (_) {
    return viewerFromUserData({});
  }
}
